// public/v3/src/components/calendar-sync-card.jsx — "Google Calendar sync" on the
// Scheduling tab. Each row links a provider's Google Workspace calendar to the
// Elation physician whose schedule it books into. Events saved on that calendar
// book Elation appointments when the title names a patient (a new chart is made
// when the event also carries a DOB and nobody matches); moving or deleting the
// event moves or cancels the appointment. Server: routes/calendarSync.js +
// services/googleCalendar/*.
//
// Its own load/save, separate from the self-scheduling draft on the page: an
// explicit Save with the server's optimistic `version`, 409 reloads.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ccmApi } from '../lib/api.js';

const STATUS_LABEL = {
  booked: 'Booked',
  needs_review: 'Not booked — see the event',
  failed: 'Not booked — Elation refused',
  indeterminate: 'Checking with Elation',
  posting: 'Booking…',
  rescheduling: 'Moving…',
  cancelling: 'Cancelling…',
  cancelled: 'Cancelled',
};

function _norm(cfg) {
  const c = cfg || {};
  return {
    version: Number.isInteger(c.version) ? c.version : 0,
    enabled: c.enabled === true,
    defaultReason: c.defaultReason || 'Office Visit',
    calendars: Array.isArray(c.calendars) ? c.calendars.map((k) => ({
      providerEmail: k.providerEmail || '', physicianId: k.physicianId || '', enabled: k.enabled === true, syncFrom: k.syncFrom || null,
    })) : [],
  };
}

function _when(v) {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function CalendarSyncCard({ canEdit, physicians }) {
  const [phase, setPhase] = useState('loading'); // loading | ready | error
  const [server, setServer] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null); // { kind, text }
  const [events, setEvents] = useState(null);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const load = useCallback(async () => {
    try {
      const data = await ccmApi.getCalendarSyncConfig();
      if (!mounted.current) return;
      setServer(data || {});
      setDraft(_norm(data && data.config));
      setPhase('ready');
    } catch (e) {
      if (mounted.current) setPhase('error');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadEvents = useCallback(() => {
    ccmApi.listCalendarSyncEvents()
      .then((r) => { if (mounted.current) setEvents((r && r.events) || []); })
      .catch(() => { if (mounted.current) setEvents('error'); });
  }, []);

  if (phase === 'loading') return <section className="card sched-card"><p className="sched-muted">Loading Google Calendar sync…</p></section>;
  if (phase === 'error') {
    return (
      <section className="card sched-card">
        <p className="sched-error" role="alert">Couldn’t load Google Calendar sync. <button type="button" className="btn sm ghost" onClick={load}>Try again</button></p>
      </section>
    );
  }

  const list = Array.isArray(physicians) ? physicians : [];
  const sa = (server && server.serviceAccount) || {};
  const stateByEmail = new Map(((server && server.calendars) || []).map((s) => [s.provider_email, s]));
  const dirty = JSON.stringify(_norm(server.config)) !== JSON.stringify(draft);
  const edit = (fn) => { setMessage(null); setDraft((d) => fn(d)); };
  const setCal = (i, patch) => edit((d) => ({ ...d, calendars: d.calendars.map((c, j) => (j === i ? { ...c, ...patch } : c)) }));

  const save = async () => {
    setSaving(true); setMessage(null);
    try {
      const body = {
        ...draft,
        calendars: draft.calendars.map((c) => ({ providerEmail: c.providerEmail.trim(), physicianId: Number(c.physicianId) || null, enabled: c.enabled })),
      };
      const data = await ccmApi.saveCalendarSyncConfig(body);
      if (!mounted.current) return;
      setServer(data); setDraft(_norm(data.config));
      setMessage({ kind: 'ok', text: 'Saved.' });
    } catch (e) {
      if (!mounted.current) return;
      const body = (e && e.body) || {};
      if (e && e.status === 409) {
        setMessage({ kind: 'warn', text: 'Someone else saved first — reloaded with their version.' });
        await load();
      } else {
        const fields = Array.isArray(body.fields) ? body.fields.map((f) => f.message).join('; ') : '';
        setMessage({ kind: 'error', text: `Couldn’t save: ${(e && e.message) || 'error'}${fields ? ` — ${fields}` : ''}` });
      }
    } finally {
      if (mounted.current) setSaving(false);
    }
  };

  return (
    <section className="card sched-card" aria-labelledby="sched-gcal-title">
      <div className="sched-card-head">
        <div>
          <h2 id="sched-gcal-title">Google Calendar sync</h2>
          <p>
            Events a provider saves on their Google Calendar book into Elation when the title starts with an existing patient’s
            name (“Jane Doe - Follow up”). Add “DOB MM/DD/YYYY” to pick between patients with the same name, or to create a new
            chart when nobody matches. Moving or deleting the event moves or cancels the appointment. Each event shows the result
            in its description.
          </p>
        </div>
      </div>

      {!sa.configured ? (
        <div className="sched-banner sched-banner-warn" role="status">
          Google Workspace isn’t connected for this practice yet. An admin needs to grant the service account access to calendars
          in the Google Admin console.
        </div>
      ) : (
        <p className="sched-muted">Service account: {sa.clientEmail}</p>
      )}

      <div className="sched-kv">
        <div className="sched-kv-row">
          <span className="sched-kv-label">Sync</span>
          <label className="sched-toggle-wrap">
            <span className="settings-toggle">
              <input type="checkbox" checked={draft.enabled} disabled={!canEdit} aria-label="Google Calendar sync on"
                onChange={(e) => edit((d) => ({ ...d, enabled: e.target.checked }))} />
              <span className="settings-toggle-slider" />
            </span>
            <span className="sched-toggle-text">{draft.enabled ? 'On' : 'Off'}</span>
          </label>
        </div>
        <div className="sched-kv-row">
          <label className="sched-kv-label" htmlFor="sched-gcal-reason">Visit reason when the title has none</label>
          <input id="sched-gcal-reason" className="sched-input" maxLength={50} value={draft.defaultReason} disabled={!canEdit}
            onChange={(e) => edit((d) => ({ ...d, defaultReason: e.target.value }))} />
        </div>
      </div>

      <div className="sched-gcal-list">
        {draft.calendars.map((c, i) => {
          const st = stateByEmail.get(c.providerEmail.trim().toLowerCase());
          return (
            <div key={i} className="sched-gcal-row">
              <input className="sched-input" type="email" placeholder="provider@emprimecare.com" aria-label="Provider’s Google email"
                value={c.providerEmail} disabled={!canEdit} onChange={(e) => setCal(i, { providerEmail: e.target.value })} />
              <select className="sched-select" aria-label="Books into Elation physician" value={String(c.physicianId || '')} disabled={!canEdit}
                onChange={(e) => setCal(i, { physicianId: e.target.value })}>
                <option value="">— Elation physician —</option>
                {list.map((p) => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
                {c.physicianId && !list.some((p) => String(p.id) === String(c.physicianId))
                  ? <option value={String(c.physicianId)}>Physician #{c.physicianId}</option> : null}
              </select>
              <label className="sched-toggle-wrap">
                <span className="settings-toggle">
                  <input type="checkbox" checked={c.enabled} disabled={!canEdit} aria-label="Calendar on" onChange={(e) => setCal(i, { enabled: e.target.checked })} />
                  <span className="settings-toggle-slider" />
                </span>
              </label>
              {canEdit ? (
                <button type="button" className="btn sm ghost" onClick={() => edit((d) => ({ ...d, calendars: d.calendars.filter((_, j) => j !== i) }))}>Remove</button>
              ) : null}
              <span className="sched-muted sched-gcal-state">
                {st && st.last_error ? `Last sync failed: ${st.last_error}` : st && st.last_synced_at ? `Synced ${_when(st.last_synced_at)}` : c.enabled && c.syncFrom ? 'Waiting for first sync' : ''}
              </span>
            </div>
          );
        })}
        {canEdit ? (
          <button type="button" className="btn sm" onClick={() => edit((d) => ({ ...d, calendars: [...d.calendars, { providerEmail: '', physicianId: '', enabled: true, syncFrom: null }] }))}>
            Add a provider calendar
          </button>
        ) : null}
        <p className="sched-muted">Only events created after a calendar is turned on are booked.</p>
      </div>

      {message ? <div className={`sched-banner sched-banner-${message.kind === 'ok' ? 'info' : message.kind}`} role="status">{message.text}</div> : null}
      {canEdit && dirty ? (
        <div className="sched-status">
          <button type="button" className="btn ghost" disabled={saving} onClick={() => { setDraft(_norm(server.config)); setMessage(null); }}>Discard</button>
          <button type="button" className="btn primary" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save calendar sync'}</button>
        </div>
      ) : null}

      <details className="sched-gcal-events" onToggle={(e) => { if (e.target.open && events === null) loadEvents(); }}>
        <summary>Recent calendar events</summary>
        {events === null ? <p className="sched-muted">Loading…</p> : null}
        {events === 'error' ? <p className="sched-error" role="alert">Couldn’t load recent events.</p> : null}
        {Array.isArray(events) && !events.length ? <p className="sched-muted">Nothing synced yet.</p> : null}
        {Array.isArray(events) && events.length ? (
          <ul className="sched-gcal-event-list">
            {events.map((ev) => (
              <li key={ev.id}>
                <strong>{ev.title || '(no title)'}</strong>
                <span className="sched-muted"> · {_when(ev.starts_at)} · {STATUS_LABEL[ev.status] || ev.status}{ev.patient_label ? ` · ${ev.patient_label}` : ''}{ev.event_deleted && ev.status !== 'cancelled' ? ' · event deleted in Google' : ''}</span>
                {ev.error ? <div className="sched-error">{ev.error}</div> : null}
              </li>
            ))}
          </ul>
        ) : null}
      </details>
    </section>
  );
}

export default CalendarSyncCard;
